import React, { useState, useEffect } from 'react';
import { FiPlus } from 'react-icons/fi';
import client from '../api/client';

export default function DrainCatheterTracker() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ deviceType: 'DRAIN', placementDate: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const fetchDevices = async () => {
    try {
      const res = await client.get('/recovery/drain-catheters');
      setDevices(res.data || []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDevices(); }, []);

  const handleAdd = async () => {
    setSaving(true);
    try {
      await client.post('/recovery/drain-catheters', {
        ...form,
        isActive: true,
      });
      setForm({ deviceType: 'DRAIN', placementDate: '', notes: '' });
      setShowForm(false);
      fetchDevices();
    } catch {
      alert('Failed to add device.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (id) => {
    try {
      await client.patch(`/recovery/drain-catheters/${id}/remove`);
      fetchDevices();
    } catch {
      alert('Failed to mark device as removed.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
      </div>
    );
  }

  const active = devices.filter(d => d.isActive);
  const removed = devices.filter(d => !d.isActive);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-white/60">Active Devices</h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1 text-sm text-primary-400 hover:text-primary-300 transition"
        >
          <FiPlus size={14} /> Add
        </button>
      </div>

      {showForm && (
        <div className="bg-white/5 rounded-xl p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <select
              value={form.deviceType}
              onChange={e => setForm({ ...form, deviceType: e.target.value })}
              className="rounded-lg bg-white/10 border-white/20 text-white px-3 py-2 text-sm"
            >
              <option value="DRAIN">Drain</option>
              <option value="CATHETER">Catheter</option>
            </select>
            <input
              type="date"
              value={form.placementDate}
              onChange={e => setForm({ ...form, placementDate: e.target.value })}
              className="rounded-lg bg-white/10 border-white/20 text-white px-3 py-2 text-sm"
            />
          </div>
          <input
            type="text"
            placeholder="Notes..."
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            className="w-full rounded-lg bg-white/10 border-white/20 text-white placeholder-white/40 px-3 py-2 text-sm"
          />
          <div className="flex gap-2">
            <button
              onClick={handleAdd}
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50 transition"
            >
              {saving ? 'Adding...' : 'Add Device'}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="px-4 py-2 bg-white/10 text-white/60 rounded-lg text-sm hover:bg-white/20 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {active.length === 0 && !showForm && (
        <p className="text-white/30 text-sm text-center py-4">No active drains or catheters</p>
      )}

      {active.map(device => (
        <div key={device.id} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-white font-medium text-sm">{device.deviceType}</span>
            </div>
            <p className="text-xs text-white/40 mt-0.5">
              Placed {device.placementDate || 'unknown'}
            </p>
            {device.notes && <p className="text-xs text-white/30 mt-1">{device.notes}</p>}
          </div>
          <button
            onClick={() => handleRemove(device.id)}
            className="text-xs text-white/30 hover:text-green-400 transition"
          >
            Mark removed
          </button>
        </div>
      ))}

      {removed.length > 0 && (
        <>
          <h3 className="text-sm font-medium text-white/40 mt-6">Removed</h3>
          {removed.map(device => (
            <div key={device.id} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl opacity-60">
              <div className="flex-1">
                <span className="text-white/70 font-medium text-sm">{device.deviceType}</span>
                <p className="text-xs text-white/30 mt-0.5">
                  {device.placementDate} &mdash; {device.removalDate}
                </p>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
